/**
 * Shared helpers for rendering people/clients in the directory:
 * deterministic persona colors + initials. Ported from the design prototype
 * (prototype/data.js) so avatars look identical to the reference.
 */

// Avatar palette for "created by" personas (navy family + neutrals).
const PERSONA_COLORS = [
    '#2f6fd1',
    '#6b54c9',
    '#2f9488',
    '#c98a2e',
    '#41995c',
    '#aa52ad',
    '#2f86ad',
    '#d1604a',
    '#4a6b8a',
    '#7d6a4a',
];

/** Deterministic background color for a persona avatar, keyed by name. */
export const personaColor = (name: string | null | undefined): string => {
    const safe = stripInternalPrefix(name ?? '');
    let h = 0;
    for (let i = 0; i < safe.length; i++) h = (h * 31 + safe.charCodeAt(i)) & 0xffff;
    return PERSONA_COLORS[h % PERSONA_COLORS.length];
};

const stripInternalPrefix = (name: string) => name.replace(/^Internal — /, '');

/** Initials for a person (e.g. "Marcus Lee" → "ML"). */
export const personInitials = (name: string | null | undefined): string => {
    const clean = stripInternalPrefix(name ?? '')
        .replace(/[^A-Za-z0-9 &]/g, ' ')
        .trim();
    const parts = clean.split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

/** First-two-word initials for a client name (e.g. "Acme Steel Co" → "AS"). */
export const clientInitials = (name: string | null | undefined): string => {
    const parts = (name ?? '').split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    return (parts[0][0] + (parts[1] ? parts[1][0] : '')).toUpperCase();
};

/** Strip the "Internal — " prefix used for staff personas before display. */
export const displayPersonaName = (name: string | null | undefined): string =>
    stripInternalPrefix(name ?? '');
